/**
 * Emits the tokens as CSS custom properties.
 *
 * This is a string builder, not a DOM writer, so it stays platform-agnostic and can run inside
 * Astro's SSR pass — the community's brand is inlined into the first byte of HTML rather than
 * applied by a script after paint. A brand that flashes the wrong colour on every page load is
 * a worse outcome than one that costs a few hundred bytes.
 */

import {
  type Brand,
  type Density,
  type ShadowLayer,
  controlGap,
  defaultBrand,
  elevation,
  focusRing,
  neutral,
  radius,
  semantic,
  space,
  touchTarget,
  typeScale,
} from "./index.js";

/** Overrides read from `communities.branding_json`. Anything absent falls back to the default. */
export interface BrandOverrides {
  primary?: string | undefined;
  primaryContrast?: string | undefined;
  secondary?: string | undefined;
  secondaryContrast?: string | undefined;
}

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Branding is tenant-supplied and lands in a `<style>` block, so it is validated rather than
 * trusted. Anything that is not a plain hex colour is dropped in favour of the default — a
 * community with a malformed brand gets the default palette, never a broken document.
 */
export function resolveBrand(overrides: BrandOverrides | null | undefined): Brand {
  const pick = (value: string | undefined, fallback: string): string =>
    typeof value === "string" && HEX.test(value) ? value : fallback;

  return {
    primary: pick(overrides?.primary, defaultBrand.primary),
    primaryContrast: pick(overrides?.primaryContrast, defaultBrand.primaryContrast),
    secondary: pick(overrides?.secondary, defaultBrand.secondary),
    secondaryContrast: pick(overrides?.secondaryContrast, defaultBrand.secondaryContrast),
  };
}

function entries(
  prefix: string,
  values: Readonly<Record<string, string | number>>,
  unit = "",
): string[] {
  return Object.entries(values).map(
    ([key, value]) =>
      `--pw-${prefix}-${kebab(key)}: ${value}${typeof value === "number" ? unit : ""};`,
  );
}

function kebab(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

/**
 * A `box-shadow` per elevation step, tinted with the ink variable rather than with black.
 *
 * `color-mix` against `var(--pw-neutral-ink)` is what keeps this honest: the shadow resolves at
 * use time, so it follows the neutral ramp instead of being a hex that happened to match it on
 * the day it was written. A warm shadow under a cool card is the specific thing this prevents,
 * and it is the reason none of these are strings in `index.ts`.
 */
function shadowVariables(): string[] {
  const layer = ({ y, blur, spread, alpha }: ShadowLayer): string =>
    `0 ${y}px ${blur}px ${spread}px ` +
    `color-mix(in srgb, var(--pw-neutral-ink) ${Math.round(alpha * 100)}%, transparent)`;

  return Object.entries(elevation).map(
    ([name, layers]) => `--pw-shadow-${name}: ${layers.map(layer).join(", ")};`,
  );
}

/**
 * The full variable set for one density. Densities are separate blocks rather than a runtime
 * switch so a judge surface and an organiser surface can coexist on one page — the arena view
 * embedded in an organiser dashboard still renders arena-sized.
 */
export function densityVariables(density: Density): string[] {
  return [
    ...entries("text", typeScale[density], "px"),
    `--pw-touch-target: ${touchTarget[density]}px;`,
    `--pw-control-gap: ${controlGap[density]}px;`,
  ];
}

export function tokensToCss(
  brandOverrides?: BrandOverrides | null,
  density: Density = "desk",
): string {
  const brand = resolveBrand(brandOverrides);

  const lines = [
    ...entries("neutral", neutral),
    ...entries("color", semantic),
    ...entries("brand", { ...brand }),
    ...entries("space", space, "px"),
    ...entries("radius", radius, "px"),
    ...shadowVariables(),
    `--pw-focus-width: ${focusRing.width}px;`,
    `--pw-focus-offset: ${focusRing.offset}px;`,
    `--pw-focus-color: ${focusRing.color};`,
    ...densityVariables(density),
  ];

  return `:root{${lines.join("")}}`;
}

/** A scoped block, for mounting an operational surface inside a desk-density page. */
export function densityToCss(density: Density, selector: string): string {
  return `${selector}{${densityVariables(density).join("")}}`;
}
